import { useMemo, type ReactNode } from "react"

import {
  NotebookCellView,
  SOFIA_MARKDOWN_COMPONENTS,
  useCoviEntityLinkPlugins,
  type ActionExecutionResult,
} from "@/app-covi"
import { Streamdown } from "streamdown"

import { formatFullTimestamp } from "@/utils/formatting/format-timestamp"

import { cellsFromSnapshot, type Notebook } from "../utils/notebookSnapshot"
import { NotebookCanvas } from "./NotebookCanvas"
import { NotebookPresentation } from "./NotebookPresentation"
import { NotebookPrintButton } from "./NotebookPrintButton"

/** Charts in a saved notebook are already settled; nothing executes on open. */
const settledExecution = (): Promise<ActionExecutionResult> =>
  Promise.resolve({ ok: true, detail: "Saved chart" })
const noopUpdate = () => {}

const NOTEBOOK_PROSE_CLASSES =
  "text-sm leading-7 text-coval-text [&_p]:mb-3 [&_p:last-child]:mb-0 " +
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 " +
  "[&_li]:mb-1 [&_a]:text-coval-info-text [&_a]:underline [&_strong]:font-semibold " +
  "[&_code]:rounded [&_code]:bg-surface-2 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em]"

type NotebookTextField =
  | "title"
  | "summary"
  | `sections.${number}.heading`
  | `sections.${number}.body`
  | `caveats.${number}`

export function NotebookContent({
  notebook,
  organizationSlug,
  workspaceId,
  creatorLabel,
  renderText,
  renderSectionActions,
  afterSections,
  titleLevel = 2,
}: {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  creatorLabel?: string
  renderText?: (field: NotebookTextField, value: string) => ReactNode
  renderSectionActions?: (index: number) => ReactNode
  afterSections?: ReactNode
  /**
   * The title's heading level. Inside the app shell the breadcrumb is the
   * page's `h1`, so the title is an `h2`; a page with no breadcrumb, such as a
   * shared link, makes the title its `h1`. Section headings sit one level below.
   */
  titleLevel?: 1 | 2
}) {
  const TitleHeading = titleLevel === 1 ? "h1" : "h2"
  const SectionHeading = titleLevel === 1 ? "h2" : "h3"
  const title = notebook.document?.title ?? notebook.title
  const remarkPlugins = useCoviEntityLinkPlugins(organizationSlug, workspaceId)
  const cells = useMemo(
    () => (notebook ? cellsFromSnapshot(notebook.id, notebook.snapshot) : []),
    [notebook],
  )
  const document = notebook?.document ?? null
  const excludedIndexes = new Set(document?.excluded_cells ?? [])
  const embeddedIndexes = useMemo(
    () =>
      new Set(
        document?.sections.flatMap((section) => section.embed_cells) ?? [],
      ),
    [document],
  )
  const appendixCells = useMemo(
    () =>
      document
        ? cells.filter(
            (cell, index) =>
              !embeddedIndexes.has(index) &&
              !document.excluded_cells?.includes(index) &&
              (cell.kind === "table" || cell.kind === "visualization"),
          )
        : cells,
    [cells, document, embeddedIndexes],
  )

  const renderCell = (index: number) => {
    const cell = cells[index]
    return cell && !excludedIndexes.has(index) ? (
      <div key={index}>
        <NotebookCellView
          key={`${organizationSlug}:${workspaceId}:${cell.id}`}
          cell={cell}
          remarkPlugins={remarkPlugins}
          executeProposal={settledExecution}
          updateActionItem={noopUpdate}
        />
      </div>
    ) : null
  }

  if (document?.content) {
    return (
      <div className="space-y-3">
        {/* Canvas headings render one level down, so a page with no
            breadcrumb names itself here. */}
        {titleLevel === 1 && <h1 className="sr-only">{title}</h1>}
        <div className="mx-auto flex max-w-6xl justify-end gap-1">
          <NotebookPresentation
            notebook={notebook}
            organizationSlug={organizationSlug}
            workspaceId={workspaceId}
          />
          <NotebookPrintButton id={notebook.id} title={title} />
        </div>
        <NotebookCanvas
          notebook={notebook}
          organizationSlug={organizationSlug}
          workspaceId={workspaceId}
        />
      </div>
    )
  }

  return (
    <article className="mx-auto w-full max-w-6xl space-y-6">
      <div className="space-y-1">
        <TitleHeading
          data-notebook-field="title"
          className="text-2xl font-semibold text-coval-text"
        >
          {renderText ? renderText("title", title) : title}
        </TitleHeading>
        <p className="text-sm text-coval-text-muted">
          Created {formatFullTimestamp(notebook.created_at)}
          {creatorLabel ? " by " : ""}
          {creatorLabel}
        </p>
      </div>

      <section className="space-y-6 rounded-lg border border-coval-border bg-surface-default p-6">
        {document ? (
          <>
            {!renderText &&
              !document.summary &&
              !document.sections.length &&
              !appendixCells.length && (
                <p className="text-coval-text-muted">
                  Your notebook is ready. Start writing with Edit notebook, or
                  ask Sofia to create something.
                </p>
              )}
            {document.summary || renderText ? (
              <div
                data-notebook-field="summary"
                className="text-base leading-7 text-coval-text"
              >
                {renderText
                  ? renderText("summary", document.summary)
                  : document.summary}
              </div>
            ) : null}
            {document.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <SectionHeading
                    data-notebook-field={`sections.${sectionIndex}.heading`}
                    className="min-w-0 flex-1 text-lg font-semibold text-coval-text"
                  >
                    {renderText
                      ? renderText(
                          `sections.${sectionIndex}.heading`,
                          section.heading,
                        )
                      : section.heading}
                  </SectionHeading>
                  {renderSectionActions?.(sectionIndex)}
                </div>
                {section.body || renderText ? (
                  <div
                    data-notebook-field={`sections.${sectionIndex}.body`}
                    className={NOTEBOOK_PROSE_CLASSES}
                  >
                    {renderText ? (
                      renderText(`sections.${sectionIndex}.body`, section.body)
                    ) : (
                      <Streamdown
                        key={`${organizationSlug}:${workspaceId}`}
                        components={SOFIA_MARKDOWN_COMPONENTS}
                        remarkPlugins={remarkPlugins}
                      >
                        {section.body}
                      </Streamdown>
                    )}
                  </div>
                ) : null}
                {section.embed_cells.length > 0 ? (
                  <div className="space-y-3">
                    {section.embed_cells.map(renderCell)}
                  </div>
                ) : null}
              </div>
            ))}
            {afterSections}
            {document.caveats.length > 0 ? (
              <div className="space-y-2">
                <SectionHeading className="text-sm font-semibold text-coval-text-muted">
                  Caveats
                </SectionHeading>
                <ul className="list-disc space-y-1 pl-5 text-sm text-coval-text">
                  {document.caveats.map((caveat, caveatIndex) => (
                    <li
                      key={caveatIndex}
                      data-notebook-field={`caveats.${caveatIndex}`}
                    >
                      {renderText
                        ? renderText(`caveats.${caveatIndex}`, caveat)
                        : caveat}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : (
          <div className="space-y-3">
            {cells.map((_cell, index) => renderCell(index))}
          </div>
        )}
      </section>

      {document && appendixCells.length > 0 ? (
        <section className="space-y-3 rounded-lg border border-coval-border bg-surface-default p-6">
          <SectionHeading className="text-sm font-semibold text-coval-text-muted">
            Additional results
          </SectionHeading>
          <div className="space-y-3">
            {appendixCells.map((cell) => renderCell(cells.indexOf(cell)))}
          </div>
        </section>
      ) : null}
    </article>
  )
}
